import { Trans } from '@lingui/macro'
import { FeeAmount } from 'elephantswapv3-sdk'
import { useMemo } from 'react'
import styled from 'styled-components/macro'

import { getPriceStrategies, matchesPriceStrategy } from './priceStrategies'

const Section = styled.section`
  background: ${({ theme }) => theme.backgroundModule};
  border-top: 1px solid ${({ theme }) => theme.backgroundOutline};
  margin: 16px -18px -14px;
  padding: 16px 18px 18px;
`

const Heading = styled.div`
  color: ${({ theme }) => theme.textSecondary};
  font-size: 13px;
  margin-bottom: 12px;
`

const Grid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(4, minmax(0, 1fr));

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToMedium`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}
`

const StrategyCard = styled.button`
  appearance: none;
  background: ${({ theme }) => theme.backgroundSurface};
  border: 1px solid ${({ theme }) => theme.backgroundOutline};
  border-radius: 12px;
  color: ${({ theme }) => theme.textPrimary};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-family: inherit;
  justify-content: space-between;
  min-height: 126px;
  padding: 12px;
  text-align: left;
  transition: border-color 120ms ease, background 120ms ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.backgroundInteractive};
  }

  &[aria-pressed='true'] {
    background: ${({ theme }) => theme.backgroundInteractive};
    border-color: ${({ theme }) => theme.accentAction};
  }

  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`

const StrategyTitle = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  display: block;
  font-size: 12px;
  margin-bottom: 8px;
`

const StrategyRange = styled.strong`
  display: block;
  font-size: 14px;
  font-weight: 500;
`

const StrategyDescription = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  display: block;
  font-size: 11px;
  line-height: 1.35;
  margin-top: 18px;
`

export function PriceStrategies({
  currentPrice,
  feeAmount,
  range,
  onSelectRange,
}: {
  currentPrice: number
  feeAmount: FeeAmount
  range?: [number, number]
  onSelectRange: (range: [number, number]) => void
}) {
  const strategies = useMemo(() => getPriceStrategies(currentPrice, feeAmount), [currentPrice, feeAmount])
  const selectedKey = strategies.find((strategy) => matchesPriceStrategy(range, strategy, feeAmount))?.key

  return (
    <Section aria-label="Price strategies">
      <Heading>
        <Trans>Price strategies</Trans>
      </Heading>
      <Grid>
        {strategies.map((strategy) => (
          <StrategyCard
            aria-pressed={strategy.key === selectedKey}
            key={strategy.key}
            onClick={() => {
              if (strategy.key !== selectedKey) onSelectRange(strategy.range)
            }}
            type="button"
          >
            <span>
              <StrategyTitle>{strategy.title}</StrategyTitle>
              <StrategyRange>{strategy.display}</StrategyRange>
            </span>
            <StrategyDescription>{strategy.description}</StrategyDescription>
          </StrategyCard>
        ))}
      </Grid>
    </Section>
  )
}
